import React, { MouseEventHandler, useState } from 'react';
import classNames from 'classnames';
import {
  Menu as ReactMenu,
  MenuItem as ReactMenuItem,
  MenuProps,
  MenuItemProps,
} from '@szhsin/react-menu';
import { DownCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import { useDispatch } from 'react-redux';
import { useCommonWidgetHandlers } from '../../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import modalActions from '@mm/state/modules/modalsState/actions';
import WidgetModel from '@mm/state/model/WidgetModel';
import { NodeProps } from 'react-flow-renderer';
import { widgetToNodeType } from '../../../use-graph';
import { NodeTypes } from '../../index';
import styles from './NodeMenu.module.css';
import { Button } from '@mm/components';

export interface INodeMenuItem {
  disabled?: boolean;
  children?: any;
  onClick: MenuItemProps['onClick'];
}

type Props = {
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
  menu?: INodeMenuItem[];
  menuButton?: MenuProps['menuButton'];
  css?: {
    menu: string;
  };
  useThreeDotButton?: boolean;
} & Pick<MenuProps, 'direction' | 'position' | 'align'>;

export const Menu = ({
  className,
  onClick,
  menu = [],
  disabled,
  direction = 'bottom',
  position = 'auto',
  align = 'end',
  menuButton,
  css,
  useThreeDotButton,
}: Props) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const onActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const threeDotButton = (
    <Button css={{ container: styles.menuButton }} color="secondary">
      <MoreOutlined className={styles.menuIcon} />
    </Button>
  );

  return (
    <div
      className={classNames(className, styles.container, {
        [styles.disabled]: disabled,
        [styles.busy]: busy,
        [styles.open]: open,
        [styles.containerThreeDotStyle]: useThreeDotButton,
      })}
      onClick={onClick}
    >
      <span
        className={classNames(styles.action, {
          [styles.actionThreeDotStyle]: useThreeDotButton,
        })}
        onClick={onActionClick}
      >
        <ReactMenu
          className={classNames(styles.menu, css?.menu)}
          direction={direction}
          position={position}
          align={align}
          offsetX={4}
          onChange={state => setOpen(state.open)}
          menuButton={
            menuButton ||
            (useThreeDotButton && threeDotButton) || <DownCircleOutlined />
          }
        >
          {menu.map((item, idx) => (
            <ReactMenuItem key={idx} {...item} />
          ))}
        </ReactMenu>
      </span>
    </div>
  );
};

export const NodeMenu = ({ widget }: { widget: WidgetModel }) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const commonWidgetHandlers = useCommonWidgetHandlers();

  const onClickDelete = () => {
    const deleteCallback = async ok => {
      if (ok) {
        await commonWidgetHandlers.delete(widget);
      }
    };

    const headerText = 'Are you sure you want to delete this action?';
    let messageText = '';

    if (widget) {
      switch (widgetToNodeType(widget)) {
        case NodeTypes.CONDITION:
          messageText +=
            'This action will remove all blocks below the Condition block';
          break;
        case NodeTypes.UNICORN_DETECTOR:
          messageText +=
            'This action will remove all blocks below the Unicorn Detector block';
          break;
      }
    }

    dispatch(
      modalActions.openGeneralConfirmModal({
        onCloseHandler: deleteCallback,
        headerText,
        messageText,
        okButtonText: 'Delete',
        okButtonStyling: { red: true },
      }),
    );
  };

  const menu = [
    {
      disabled: false,
      children: 'Delete',
      onClick: onClickDelete,
    },
  ];
  return <Menu menu={menu} direction="bottom" />;
};

export const withMenu =
  (Component: React.FC<NodeProps<{ widget: WidgetModel }>>) => props => {
    return (
      <div className={styles.hoverTracker}>
        <Component {...props}>
          <NodeMenu widget={props.data.widget} />
        </Component>
      </div>
    );
  };
